import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  listStaffCommands, getStaffCommandOptions, createStaffCommand, updateStaffCommand,
  deleteStaffCommand,
} from "../../api/staffCommandsApi";
import LoadingState from "./LoadingState";
import ConfirmModal from "./ConfirmModal";

const emptyForm = () => ({
  command: "", description: "", handler_type: "tool", tool_id: "",
  arguments: JSON.stringify({ permission: "", arguments: {} }, null, 2),
  renderer: "text", enabled: true, sort_order: 0,
});
const errorMessage = (error) => {
  const detail = error?.response?.data?.detail;
  return typeof detail === "string" ? detail
    : Array.isArray(detail) ? detail.map((item) => item.msg).join("; ")
      : error?.message || "Unable to update staff commands.";
};
const inputClass = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm";

export default function StaffCommandsPanel({ botId, clientId }) {
  const [commands, setCommands] = useState([]);
  const [options, setOptions] = useState({ tools: [], permissions: [], renderers: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editor, setEditor] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleting, setDeleting] = useState(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setEditor(null);
    setDeleting(null);
    setError("");
    Promise.all([listStaffCommands(botId), getStaffCommandOptions(botId)])
      .then(([rows, config]) => { if (active) { setCommands(rows); setOptions(config); } })
      .catch((err) => { if (active) setError(errorMessage(err)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [botId, reload]);

  const refresh = () => setReload((value) => value + 1);
  const change = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const selectedTool = options.tools.find((tool) => tool.id === form.tool_id);
  const open = (command) => {
    setError("");
    setSuccess("");
    setEditor(command || { id: null });
    setForm(command ? {
      ...command,
      arguments: JSON.stringify({
        permission: command.permission_resource ? `${command.permission_resource}.${command.permission_action}` : "",
        arguments: command.arguments,
      }, null, 2), tool_id: command.tool_id || "",
      renderer: command.renderer || "text",
    } : emptyForm());
  };
  const perform = async (operation, message) => {
    setBusy(true);
    setError("");
    setSuccess("");
    try { await operation(); setSuccess(message); refresh(); }
    catch (err) { setError(errorMessage(err)); }
    finally { setBusy(false); }
  };
  const save = async (event) => {
    event.preventDefault();
    const help = form.handler_type === "help";
    let args = {};
    let permission;
    try {
      if (!help) {
        const definition = JSON.parse(form.arguments);
        if (!definition || Array.isArray(definition) || typeof definition !== "object") {
          throw new Error("Command definition must be a JSON object.");
        }
        if (Object.keys(definition).some((key) => !["permission", "arguments"].includes(key))) {
          throw new Error("Only permission and arguments are allowed in the command definition.");
        }
        permission = options.permissions.find(({ resource, action }) =>
          definition.permission === `${resource}.${action}`);
        if (!permission) throw new Error("Permission must match an available permission.");
        args = definition.arguments;
        if (!args || Array.isArray(args) || typeof args !== "object") {
          throw new Error("Nested arguments must be a JSON object.");
        }
      }
    } catch (err) {
      setError(`Invalid command definition: ${err.message}`);
      return;
    }
    const payload = {
      command: help ? "help" : form.command.trim(), description: form.description.trim(),
      handler_type: form.handler_type, tool_id: help ? null : form.tool_id,
      permission_resource: help ? null : permission.resource, permission_action: help ? null : permission.action,
      arguments: help ? {} : args, renderer: help ? null : form.renderer,
      enabled: form.enabled, sort_order: Number(form.sort_order),
    };
    await perform(() => editor.id ? updateStaffCommand(botId, editor.id, payload)
      : createStaffCommand(botId, payload), "Command saved.");
  };

  if (loading) return <LoadingState label="Loading staff commands..." />;
  return (
    <section aria-label="Staff Commands" className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-slate-900">Staff Commands</h2>
        <p className="mt-1 text-sm text-slate-600">
          Supports /, # and @ prefixes. Access requires an active user with verified WhatsApp login enabled,
          access to this bot&apos;s client, and the required permission. Manage users and roles in Client Team.
        </p>
        <p className="mt-2 text-sm text-slate-600">
          Create tools in {clientId ? <Link className="text-[#096dea] underline" to={`/clients/${encodeURIComponent(clientId)}/tools`}>Client Tools</Link> : "Client Tools"},
          {" "}set metadata <code>{'{"usage":"staff_command"}'}</code>, then assign them to this bot under Tools.
          Enabled command tools appear here; existing staff-only assignments are also supported.
          Read-only commands are supported; arguments are validated against the tool schema.
        </p>
      </div>
      {error && <div role="alert" className="rounded-xl bg-rose-50 p-3 text-sm text-rose-700">
        {error} <button type="button" onClick={refresh} disabled={busy} className="underline">Reload</button>
      </div>}
      {success && <p role="status" className="text-sm text-emerald-700">{success}</p>}
      <div className="flex flex-wrap gap-3">
        <button type="button" disabled={busy} onClick={() => open(null)} className="rounded-xl bg-[#096dea] px-4 py-2 text-sm font-semibold text-white">Add command</button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50"><tr>{["Command", "Description", "Tool", "Permission", "Status", "Actions"].map((label) => <th key={label} className="p-3">{label}</th>)}</tr></thead>
          <tbody>{commands.map((command) => <tr key={command.id} className="border-t border-slate-100">
            <td className="p-3 font-mono">/{command.command}</td><td className="p-3">{command.description}</td>
            <td className="p-3">{command.handler_type === "help" ? "Command catalogue" : options.tools.find((tool) => tool.id === command.tool_id)?.name || "Tool unavailable"}</td>
            <td className="p-3">{command.permission_resource ? `${command.permission_resource}.${command.permission_action}` : "Eligible users"}</td>
            <td className="p-3">{command.enabled ? "Enabled" : "Disabled"}</td>
            <td className="p-3 whitespace-nowrap">
              <button type="button" disabled={busy} onClick={() => open(command)} className="mr-3 text-[#096dea]" aria-label={`Edit ${command.command}`}>Edit</button>
              <button type="button" disabled={busy} onClick={() => setDeleting(command)} className="text-rose-700" aria-label={`Delete ${command.command}`}>Delete</button>
            </td>
          </tr>)}</tbody>
        </table>
        {!commands.length && <p className="p-4 text-sm text-slate-500">No staff commands configured. Add help and map your shortcuts to tools.</p>}
      </div>
      {editor && <form onSubmit={save} aria-label="Command editor" className="space-y-4 rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="font-semibold">{editor.id ? "Edit command" : "Add command"}</h3>
        <fieldset disabled={busy} className="space-y-4">
          <label className="block text-sm">Handler
            <select className={inputClass} value={form.handler_type} onChange={(e) => {
              const help = e.target.value === "help";
              setForm((current) => ({ ...current, handler_type: e.target.value, command: help ? "help" : "",
                arguments: emptyForm().arguments, tool_id: "", renderer: "text" }));
            }}><option value="tool">Tool</option><option value="help">Help catalogue</option></select>
          </label>
          <label className="block text-sm">Command key (without prefix)
            <input className={inputClass} required pattern="[a-z][a-z0-9_]{0,63}" maxLength={64} readOnly={form.handler_type === "help"}
              value={form.command} onChange={(e) => change("command", e.target.value)} />
          </label>
          <label className="block text-sm">Short description
            <input className={inputClass} required maxLength={500} value={form.description} onChange={(e) => change("description", e.target.value)} />
          </label>
          {form.handler_type === "tool" && <>
            <label className="block text-sm">Assigned command tool
              <select className={inputClass} required value={form.tool_id} onChange={(e) => {
                const tool = options.tools.find((item) => item.id === e.target.value);
                setForm((current) => ({ ...current, tool_id: e.target.value,
                  arguments: JSON.stringify({ permission: tool?.metadata?.staff_permission || "", arguments: {} }, null, 2),
                  renderer: "json" }));
              }}>
                <option value="">Select a tool</option>
                {form.tool_id && !selectedTool && <option value={form.tool_id} disabled>Tool unavailable</option>}
                {options.tools.map((tool) => <option key={tool.id} value={tool.id}>{tool.name}</option>)}
              </select>
            </label>
            <label className="block text-sm">Tool arguments (JSON object)
              <textarea className={`${inputClass} font-mono`} rows={5} value={form.arguments} onChange={(e) => change("arguments", e.target.value)} />
            </label>
            <p className="text-sm text-slate-600">
              Define permission and nested arguments, for example <code>{'{"permission":"appointments.read","arguments":{"period":"today"}}'}</code>.
              {" "}Permission is checked before execution; only nested arguments are sent to the tool.
              {" "}Available permissions: {options.permissions.map(({ resource, action }) => `${resource}.${action}`).join(", ") || "None"}.
            </p>
            {selectedTool && <details className="text-sm"><summary>Tool input schema</summary>
              <pre className="overflow-auto rounded-lg bg-slate-50 p-3 text-xs">{JSON.stringify(selectedTool.input_schema, null, 2)}</pre>
            </details>}
            <label className="block text-sm">Output format
              <select className={inputClass} value={form.renderer} onChange={(e) => change("renderer", e.target.value)}>
                {options.renderers.map((renderer) => <option key={renderer} value={renderer}>{renderer}</option>)}
              </select>
            </label>
          </>}
          <label className="block text-sm">Display order
            <input className={inputClass} type="number" min={0} max={2147483647} required value={form.sort_order} onChange={(e) => change("sort_order", e.target.value)} />
          </label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.enabled} onChange={(e) => change("enabled", e.target.checked)} />Enabled</label>
          <div className="flex gap-3">
            <button type="submit" className="rounded-xl bg-[#096dea] px-4 py-2 text-sm font-semibold text-white">{busy ? "Saving..." : "Save command"}</button>
            <button type="button" onClick={() => setEditor(null)} className="rounded-xl border border-slate-200 px-4 py-2 text-sm">Cancel</button>
          </div>
        </fieldset>
      </form>}
      {deleting && <ConfirmModal open title="Delete staff command?" description={`Remove /${deleting.command}? This does not delete the tool or change user permissions.`}
        confirmLabel="Delete command" loading={busy} onCancel={() => { if (!busy) setDeleting(null); }}
        onConfirm={() => perform(() => deleteStaffCommand(botId, deleting.id), "Command deleted.")} />}
    </section>
  );
}
